export type ProjectInvoiceStatus = 'TO_INVOICE' | 'SENT' | 'OVERDUE' | 'LOCKED';

export type ProjectOtherCostCategory = 'TRAVEL' | 'RESTAURANT' | 'CONSULTANCY' | 'TRANSLATION' | 'OTHER';

export type ProjectFinanceAuthorizationAction = 'AUTO_LOCK' | 'MANUAL_LOCK' | 'MANUAL_UNLOCK';

export type ProjectFinanceManualAuthorizationAction = Exclude<ProjectFinanceAuthorizationAction, 'AUTO_LOCK'>;

export interface ProjectFinanceAttachment {
  attachmentId: number;
  fileNameOriginal: string;
  url: string;
  publicUrl?: string | null;
}

export interface ProjectFinanceUser {
  userId: number;
  firstName: string;
  lastName: string;
}


export interface ProjectInvoice {
  projectInvoiceId: number;
  projectId: number;
  instalmentTypeCode: string;
  instalmentTypeName: string;
  percent: number;
  amount: number;
  dueDate: string | null;
  paidDate: string | null;
  bankReceived: string | null;
  reference: string | null;
  note: string | null;
  attachment: ProjectFinanceAttachment | null;
  sentDate: string | null;
  status: ProjectInvoiceStatus;
}

export interface ProjectInvoiceUpdatePayload {
  dueDate: string;
  paidDate: string | null;
  bankReceived: string | null;
  reference: string | null;
  note: string | null;
}

export interface ProjectFinanceAuthorizationHistory {
  projectFinanceAuthorizationHistoryId: number;
  user: ProjectFinanceUser | null;
  action: ProjectFinanceAuthorizationAction;
  reason: string | null;
  createTime: string | null;
}

export interface ProjectFinanceAuthorizationOverridePayload {
  action: ProjectFinanceManualAuthorizationAction;
  reason: string;
}


export interface ProjectFinanceCost {
  projectFinanceCostId: number;
  resourceUser: ProjectFinanceUser | null;
  hoursWorked: number;
  hourlyRate: number;
  rowTotal: number;
  activityDescription: string | null;
  date: string;
}

export interface ProjectFinanceCostPayload {
  resourceUserId: number;
  hoursWorked: number;
  hourlyRate: number;
  activityDescription: string | null;
  date: string;
}

export interface ProjectFinanceOtherCost {
  projectFinanceOtherCostId: number;
  category: ProjectOtherCostCategory;
  description: string;
  amount: number;
  date: string;
  attachment: ProjectFinanceAttachment | null;
  notes: string | null;
}

export interface ProjectFinanceOtherCostPayload {
  category: ProjectOtherCostCategory;
  description: string;
  amount: number;
  date: string;
  notes: string | null;
}

export interface ProjectFinanceBalance {
  totalIncome: number;
  totalTestingCosts: number;
  totalOtherCosts: number;
  totalCosts: number;
  margin: number;
  projectStatus: string;
}

export interface ProjectPaymentsReportRow {
  projectInvoiceId: number;
  projectId: number;
  projectCode: string;
  projectName: string;
  clientReference: string;
  amount: number;
  dueDate: string;
  status: ProjectInvoiceStatus;
  notes: string | null;
}

export interface ProjectFinanceExcelFile {
  fileName: string;
  mimeType: string;
  base64: string;
}

export const PROJECT_FINANCE_DEFAULT_CURRENCY = 'EUR';

export const PROJECT_FINANCE_INVOICE_MAX_SIZE_BYTES = 10 * 1024 * 1024;

export const PROJECT_FINANCE_PROFITABLE_STATUS = 'PROFITABLE';

export const PROJECT_INVOICE_STATUS_LABELS: Record<ProjectInvoiceStatus, string> = {
  TO_INVOICE: 'To Invoice',
  SENT: 'Sent',
  OVERDUE: 'Overdue',
  LOCKED: 'Locked'
};

export const PROJECT_OTHER_COST_CATEGORY_LABELS: Record<ProjectOtherCostCategory, string> = {
  TRAVEL: 'Travel',
  RESTAURANT: 'Restaurant',
  CONSULTANCY: 'Consultancy',
  TRANSLATION: 'Translation',
  OTHER: 'Other'
};

export const projectInvoiceStatusLabel = (status: string | null | undefined): string =>
  status ? (PROJECT_INVOICE_STATUS_LABELS[status as ProjectInvoiceStatus] ?? status) : '-';

export const projectInvoiceStatusClass = (status: string | null | undefined): string =>
  `finance-status-badge status-${(status ?? '').toLowerCase().replace(/_/g, '-')}`;
